import React, {
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
  useEffect,
  useCallback,
} from "react";
import { Maximize2, Minimize2 } from "lucide-react";
import { useAppStore } from "../../store/appStore";
import Hls from 'hls.js';


const VideoPlayer = forwardRef(
  ({ isExpanded, onToggleExpand, hideExpandButton = false, videoUrl, onTimeUpdate, onEnded, onError }, ref) => {
    const [key, setKey] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);
    const [loadedVideos, setLoadedVideos] = useState({});
    const [preloadVideo, setPreloadVideo] = useState(null);
    const [isPreloading, setIsPreloading] = useState(false);
    const [preloadProgress, setPreloadProgress] = useState(0);
    
    // 从Zustand获取当前时间，用于同步
    const currentGlobalTime = useAppStore(state => state.videoState.currentTime);
    const isGlobalPlaying = useAppStore(state => state.videoState.isPlaying);
    const activeRobotId = useAppStore(state => state.selection.activeRobotId);
    const updateVideoState = useAppStore(state => state.updateVideoState);
    const timeframe = useAppStore(state => state.timeframe);
    const videoState = useAppStore(state => state.videoState);

    let hasSetInitialTime = false; // ✅ 添加顶部变量，用于 HLS 只设置一次 currentTime
    
    // 跟踪refs
    const videoRefs = useRef([]);
    const loadingTimerRef = useRef(null);
    const rafIdRef = useRef(null);
    const lastSyncedTimeRef = useRef(0);
    const lastActiveRobotIdRef = useRef(null);
    const lastTimeframeRef = useRef(null);
    const handlePlayTimeoutRef = useRef(null);
    const hasUserInteracted = useRef(false);
    const activeIndexRef = useRef(0);
    const preloadVideoRef = useRef(null);
    const isTransitioning = useRef(false);

    // 预加载状态管理
    const preloadStateRef = useRef({
      isPreloading: false,
      startTime: null,
      progress: 0,
      video: null
    });

    // 构建高清视频URL，不依赖于 timeframe
    const highResVideoFiles = Array.from({length: 10}, (_, i) => {
      const timePart = timeframe === "11AM" ? "day" : "night";
      return `/${timePart}/videos/video${i + 1}.mp4`;
    });
    
    // 计算当前激活的视频索引
    const activeIndex = activeRobotId ? activeRobotId - 1 : 0;
    
    // 使用requestAnimationFrame实现平滑的时间更新
    useEffect(() => {
      let rafId;
      let lastUpdateTime = 0;
      const UPDATE_INTERVAL = 1000 / 30; // 30fps
    
      const updateTime = (timestamp) => {
        const activeVideo = videoRefs.current[activeIndexRef.current];
        if (!activeVideo || activeVideo.paused) {
          rafId = requestAnimationFrame(updateTime);
          return;
        }

        // 获取实际的视频时间
        const videoTime = activeVideo.currentTime;
        
        // 更新全局状态
        updateVideoState({ time: videoTime }, 'videoPlayer');
        
        // 继续下一帧
        rafId = requestAnimationFrame(updateTime);
      };
    
      // 只在视频播放时启动更新
      if (isGlobalPlaying) {
        rafId = requestAnimationFrame(updateTime);
      }
    
      return () => {
        if (rafId) {
          cancelAnimationFrame(rafId);
        }
      };
    }, [isGlobalPlaying, activeIndex, updateVideoState]);
    
    
    // 优化监听全局播放状态变化
    // useEffect(() => {
    //   const activeVideo = videoRefs.current[activeIndex];
    //   if (!activeVideo || isLoading) return;
      
    //   // 减少防抖时间
    //   if (window.lastPlayStateChangeTime && (Date.now() - window.lastPlayStateChangeTime < 50)) {
    //     return;
    //   }
      
    //   window.lastPlayStateChangeTime = Date.now();
      
    //   if (isGlobalPlaying && activeVideo.paused) {
    //     console.log(`[VideoPlayer ${new Date().toISOString()}] ▶️ 开始播放 (从全局状态)`);
        
    //     // 清除之前的播放定时器
    //     if (handlePlayTimeoutRef.current) {
    //       clearTimeout(handlePlayTimeoutRef.current);
    //     }
        
    //     // 减少延迟
    //     handlePlayTimeoutRef.current = setTimeout(() => {
    //       activeVideo.play().catch(err => {
    //         console.warn(`[VideoPlayer ${new Date().toISOString()}] ⚠️ 播放错误:`, err);
            
    //         // 特殊处理：浏览器要求用户交互后才能播放视频
    //         if (err.name === 'NotAllowedError') {
    //           // 添加一个一次性点击监听器
    //           const clickToPlay = () => {
    //             activeVideo.play().catch(() => {});
    //             document.removeEventListener('click', clickToPlay);
    //           };
    //           document.addEventListener('click', clickToPlay, { once: true });
    //         }
            
    //         // 处理节能中断错误
    //         if (err.name === 'AbortError' && err.message.includes('power')) {
    //           // 尝试静音播放
    //           activeVideo.muted = true;
    //           activeVideo.play().then(() => {
    //             // 播放成功后恢复声音
    //             setTimeout(() => { activeVideo.muted = false; }, 100);
    //           }).catch(() => {});
    //         }
    //       });
    //     }, 20);
    //   } else if (!isGlobalPlaying && !activeVideo.paused) {
    //     console.log(`[VideoPlayer ${new Date().toISOString()}] ⏸️ 暂停播放 (从全局状态)`);
    //     activeVideo.pause();
    //   }
    // }, [isGlobalPlaying, activeIndex, isLoading]);
//     useEffect(() => {
//       const activeVideo = videoRefs.current[activeIndex];
//       if (!activeVideo || isLoading) return;
    
//       const currentVideoTime = activeVideo.currentTime;

// // 强制从 store 拿最新的 time
// const { isHls, time: hlsOffset } = useAppStore.getState().videoState;

// let syncedTime = currentVideoTime;
// if (isHls && typeof hlsOffset === 'number') {
//   syncedTime = currentVideoTime + hlsOffset;
// }

    
//       console.log('[SYNC] currentVideoTime:', currentVideoTime);
//       console.log('[SYNC] videoState.time:', videoState.time);
//       console.log('[SYNC] syncedTime → 更新状态:', syncedTime);
    
//       if (Math.abs(syncedTime - lastSyncedTimeRef.current) > 0.01) {
//         lastSyncedTimeRef.current = syncedTime;
//         updateVideoState({ time: syncedTime }, 'videoPlayer_hls_sync');
//         if (onTimeUpdate) {
//           onTimeUpdate(syncedTime);
//         }
//       }
//     }, [activeIndex, onTimeUpdate, updateVideoState, currentGlobalTime, isGlobalPlaying, isLoading]);
    
    
    // 改进的监听时间变化效果
    useEffect(() => {
      const activeVideo = videoRefs.current[activeIndexRef.current];
      if (!activeVideo || isLoading) return;
    
      const videoState = useAppStore.getState().videoState;
    
      // ✅ 新增：如果是 HLS 且未设置起点，则不要跳转时间
      const isHls = videoState.isHls;
      const hlsBaseTime = videoState.hlsBaseTime || 0;
      const hlsLoadingInProgress = isHls && !hasSetInitialTime;
    
      if (hlsLoadingInProgress) {
        console.log("[VideoPlayer] ⏳ 正在加载 HLS，暂不进行时间跳转");
        return;
      }
  
      // 添加时间范围检查
      const MAX_VIDEO_DURATION = 1800; // 30分钟
      
      // 计算实际需要跳转的视频时间
      let targetVideoTime = currentGlobalTime;
      if (isHls) {
        // HLS视频需要减去基准时间
        targetVideoTime = currentGlobalTime - hlsBaseTime;
      }
      
      // 确保时间在有效范围内
      targetVideoTime = Math.min(Math.max(0, targetVideoTime), MAX_VIDEO_DURATION);
      
      const timeDiff = Math.abs(activeVideo.currentTime - targetVideoTime);
      if (timeDiff > 0.5 && targetVideoTime >= 0) {
        const timestamp = new Date().toISOString();
        console.log(`[VideoPlayer ${timestamp}] 📍 开始跳转到 ${targetVideoTime.toFixed(2)}秒 ${isHls ? `(HLS基准时间: ${hlsBaseTime}s)` : ''}`);
    
        const wasPlaying = !activeVideo.paused;
    
        try {
          activeVideo.currentTime = targetVideoTime;
          lastSyncedTimeRef.current = targetVideoTime;
    
          if (wasPlaying || isGlobalPlaying) {
            console.log(`[VideoPlayer ${timestamp}] ▶️ 尝试恢复播放`);
            const playPromise = activeVideo.play();
            if (playPromise !== undefined) {
              playPromise.catch(error => {
                console.warn(`[VideoPlayer ${timestamp}] ⚠️ 播放失败:`, error);
                activeVideo.muted = true;
                activeVideo.play().then(() => {
                  setTimeout(() => { activeVideo.muted = false; }, 100);
                }).catch(() => {});
              });
            }
          }
        } catch (err) {
          console.error(`[VideoPlayer ${timestamp}] ❌ 设置视频时间失败:`, err);
        }
      }
    }, [currentGlobalTime, isGlobalPlaying, activeIndex, isLoading]);
    
    // 优化监听activeRobotId变化
    useEffect(() => {
      const timestamp = new Date().toISOString();
      // 只在robotId真正改变时执行
      if (lastActiveRobotIdRef.current === activeRobotId) return;
      
      // 记录新的activeRobotId
      const previousRobotId = lastActiveRobotIdRef.current;
      lastActiveRobotIdRef.current = activeRobotId;
      
      // 如果 activeRobotId 为 null，不执行切换
      if (!activeRobotId) {
        console.log(`[VideoPlayer ${timestamp}] ⚠️ 忽略无效的 robotId`);
        return;
      }
      
      // 立即更新 activeIndexRef 为新的索引值
      activeIndexRef.current = activeRobotId - 1;
      
      console.log(`[VideoPlayer ${timestamp}] 🔄 切换视频: Robot ${previousRobotId || 'none'} -> Robot ${activeRobotId} (索引: ${activeIndex})`);
      
      // 立即清除所有可能影响的标志
      window.isPlaybackInProgress = false;
      
      // 保存当前时间
      const currentTime = videoRefs.current[activeIndexRef.current]?.currentTime || currentGlobalTime;
      
      // 暂停所有视频
      videoRefs.current.forEach((video, index) => {
        if (video && !video.paused) {
          try {
            video.pause();
          } catch (e) {
            // 忽略错误
          }
        }
      });

      // 设置加载状态
      setIsLoading(true);
      
      // 使用Promise处理视频切换
      const switchVideo = async () => {
        const newVideo = videoRefs.current[activeIndexRef.current];
        if (!newVideo) return;

        try {
          // 确保视频已暂停
          if (!newVideo.paused) {
            newVideo.pause();
          }

          // 等待视频就绪
          if (newVideo.readyState < 3) { // HAVE_FUTURE_DATA
            await new Promise((resolve, reject) => {
              const onCanPlay = () => {
                newVideo.removeEventListener('canplay', onCanPlay);
                newVideo.removeEventListener('error', onError);
                resolve();
              };
              const onError = (error) => {
                newVideo.removeEventListener('canplay', onCanPlay);
                newVideo.removeEventListener('error', onError);
                reject(error);
              };
              newVideo.addEventListener('canplay', onCanPlay);
              newVideo.addEventListener('error', onError);
            });
          }

          // 设置时间
          const videoState = useAppStore.getState().videoState;
          const targetTime = videoState.isHls ? 
            (videoState.time - (videoState.hlsBaseTime || 0)) : 
            videoState.time;

          if (Math.abs(newVideo.currentTime - targetTime) > 0.5) {
            newVideo.currentTime = targetTime;
          }

          // 清除加载状态
          setIsLoading(false);

          // 如果需要播放，确保视频已完全加载并等待一小段时间
          if (videoState.isPlaying) {
            // 等待视频完全加载
            if (newVideo.readyState < 4) { // HAVE_ENOUGH_DATA
              await new Promise(resolve => {
                const onCanPlayThrough = () => {
                  newVideo.removeEventListener('canplaythrough', onCanPlayThrough);
                  resolve();
                };
                newVideo.addEventListener('canplaythrough', onCanPlayThrough);
              });
            }

            // 添加短暂延迟确保状态稳定
            await new Promise(resolve => setTimeout(resolve, 100));

            // 确保视频仍然需要播放
            if (videoState.isPlaying) {
              try {
                const playPromise = newVideo.play();
                if (playPromise) {
                  await playPromise;
                }
              } catch (playError) {
                console.warn(`[VideoPlayer ${timestamp}] ⚠️ 播放失败，尝试静音播放:`, playError);
                // 尝试静音播放
                newVideo.muted = true;
                await newVideo.play();
                // 恢复声音
                setTimeout(() => {
                  newVideo.muted = false;
                }, 100);
              }
            }
          }

          console.log(`[VideoPlayer ${timestamp}] ✅ 视频切换完成`);
        } catch (error) {
          console.error(`[VideoPlayer ${timestamp}] ❌ 视频切换失败:`, error);
          setIsLoading(false);
          
          // 如果是 HLS 视频，尝试回退到 MP4
          if (videoUrl.includes('.m3u8')) {
            console.log(`[VideoPlayer ${timestamp}] 🔄 尝试回退到 MP4 视频`);
            const timePart = timeframe === "11AM" ? "day" : "night";
            const mp4Url = `/${timePart}/videos/video${activeRobotId}.mp4`;
            updateVideoState({
              videoUrl: mp4Url,
              isHls: false,
              hlsBaseTime: 0,
              hlsResumeTime: null
            }, 'hls_fallback');
          }
        }
      };

      // 执行视频切换
      switchVideo();
      
    }, [activeRobotId, activeIndex, currentGlobalTime, timeframe, videoUrl]);
    
    // 监听timeframe变化
    useEffect(() => {
      // 只在timeframe真正改变时执行
      if (lastTimeframeRef.current === timeframe) return;
      lastTimeframeRef.current = timeframe;
      
      console.log(`[VideoPlayer ${new Date().toISOString()}] 🕒 Timeframe变化，重新加载所有视频`);
      setKey(prev => prev + 1);
      setLoadedVideos({});
      setIsLoading(true);
    }, [timeframe]);
    
    // 优化的组件清理
    useEffect(() => {
      return () => {
        // 确保清除所有超时和动画帧
        if (rafIdRef.current) {
          cancelAnimationFrame(rafIdRef.current);
          rafIdRef.current = null;
        }
        
        if (loadingTimerRef.current) {
          clearTimeout(loadingTimerRef.current);
          loadingTimerRef.current = null;
        }
        
        if (handlePlayTimeoutRef.current) {
          clearTimeout(handlePlayTimeoutRef.current);
          handlePlayTimeoutRef.current = null;
        }
        
        // 确保清除全局标志
        window.timeJumpInProgress = false;
        window.isPlaybackInProgress = false;
        
        // 确保所有视频已暂停
        videoRefs.current.forEach(video => {
          if (video) {
            try {
              video.pause();
            } catch (e) {
              // 忽略错误
            }
          }
        });
      };
    }, []);

    // Add this cleanup effect to ensure all flags and timeouts are cleared
    useEffect(() => {
      return () => {
        if (loadingTimerRef.current) {
          clearTimeout(loadingTimerRef.current);
        }
        if (handlePlayTimeoutRef.current) {
          clearTimeout(handlePlayTimeoutRef.current);
        }
      };
    }, []);

    useEffect(() => {
      const markUserInteraction = () => {
        hasUserInteracted.current = true;
      };
      
      // 监听用户交互事件
      document.addEventListener('click', markUserInteraction);
      document.addEventListener('keydown', markUserInteraction);
      document.addEventListener('touchstart', markUserInteraction);
      
      return () => {
        document.removeEventListener('click', markUserInteraction);
        document.removeEventListener('keydown', markUserInteraction);
        document.removeEventListener('touchstart', markUserInteraction);
      };
    }, []);


    
    useEffect(() => {
      const video = videoRefs.current[activeIndexRef.current];
      if (!video || !videoUrl) return;
    
      const hlsResumeTime = videoState.hlsResumeTime;
      const robotId = activeRobotId;
      const updateVideoState = useAppStore.getState().updateVideoState;
      const timeframe = useAppStore.getState().timeframe;
    
      // 清除旧 HLS 实例
      if (video._hlsInstance) {
        video._hlsInstance.destroy();
        video._hlsInstance = null;
      }
    
      if (videoUrl.endsWith('.m3u8')) {
        if (Hls.isSupported()) {
          const hls = new Hls({
            maxBufferLength: 10,
            maxMaxBufferLength: 20,
            maxBufferSize: 30 * 1000 * 1000,
            maxBufferHole: 0.1,
            lowLatencyMode: true,
            backBufferLength: 10,
            manifestLoadingTimeOut: 5000,
            manifestLoadingMaxRetry: 2,
            levelLoadingTimeOut: 5000,
            levelLoadingMaxRetry: 2,
            fragLoadingTimeOut: 5000,
            fragLoadingMaxRetry: 2,
            startLevel: -1,
            abrEwmaDefaultEstimate: 500000,
            abrMaxWithRealBitrate: true,
            testBandwidth: true,
            progressive: true,
            lowLatencyMode: true,
            enableWorker: true,
            startPosition: -1
          });
          
          hls.loadSource(videoUrl);
          hls.attachMedia(video);
    
          hls.on(Hls.Events.MANIFEST_PARSED, () => {
            console.log("[VideoPlayer] ✅ m3u8 解析完成，尝试播放");
            
            // 开始预加载 MP4 视频
            const mp4Url = `/${timeframe === "11AM" ? "day" : "night"}/videos/video${activeRobotId}.mp4`;
            const preloadVideo = document.createElement('video');
            preloadVideo.preload = "auto";
            preloadVideo.src = mp4Url;
            preloadVideo.currentTime = videoState.hlsResumeTime;
            preloadVideo.style.display = "none";
            document.body.appendChild(preloadVideo);
            
            preloadVideoRef.current = preloadVideo;
            
            // 监听预加载进度
            preloadVideo.addEventListener('progress', () => {
              if (preloadVideo.buffered.length > 0) {
                const bufferedEnd = preloadVideo.buffered.end(preloadVideo.buffered.length - 1);
                console.log(`[Preload] 预加载进度: ${bufferedEnd}`);
              }
            });
            
            // 当预加载完成时
            preloadVideo.addEventListener('canplay', () => {
              console.log(`[Preload] ✅ MP4 预加载完成，准备就绪`);
            });
          });

          hls.on(Hls.Events.ERROR, (event, data) => {
            if (data.fatal) {
              switch (data.type) {
                case Hls.ErrorTypes.NETWORK_ERROR:
                  console.warn("⚠️ HLS 网络错误，尝试恢复");
                  hls.startLoad();
                  break;
                case Hls.ErrorTypes.MEDIA_ERROR:
                  console.warn("⚠️ HLS 媒体错误，尝试恢复");
                  hls.recoverMediaError();
                  break;
                default:
                  console.error("❌ HLS 致命错误，无法恢复");
                  hls.destroy();
                  // 切换到 MP4 作为后备
                  const mp4Url = `/${timeframe === "11AM" ? "day" : "night"}/videos/video${robotId}.mp4`;
                  updateVideoState({
                    videoUrl: mp4Url,
                    time: hlsResumeTime || 0,
                    isPlaying: true,
                    isHls: false,
                    hlsResumeTime: null
                  }, 'hls_error_fallback');
                  break;
              }
            }
          });

          video._hlsInstance = hls;
        }
      } else {
        // 对于 MP4 视频，使用更智能的加载策略
        video.src = videoUrl;
        
        // 快速设置时间，不等待元数据
        if (hlsResumeTime !== null) {
          requestAnimationFrame(() => {
            try {
              video.currentTime = hlsResumeTime;
              // 立即更新全局状态
              updateVideoState({
                time: hlsResumeTime,
                isHls: false,
                hlsResumeTime: null,
                hlsBaseTime: 0,
                isPlaying: true
              }, 'hls_to_mp4_instant');
            } catch (e) {
              console.warn('快速设置时间失败，回退到常规方式');
            }
          });
        }
      }
    }, [videoUrl, activeIndex, isGlobalPlaying, videoState.hlsResumeTime, activeRobotId]);
        
      
    
    // 优化的视频加载处理函数
    const handleVideoLoad = useCallback((index) => {
      const video = videoRefs.current[index];
      if (!video) return;
      
      // 降低可播放的标准
      const canPlay = video.readyState >= 2; // 降低从HAVE_FUTURE_DATA到HAVE_CURRENT_DATA
      
      // 防止重复处理
      if (loadedVideos[index]) return;
      
      console.log(`[VideoPlayer ${new Date().toISOString()}] 🔄 视频 ${index + 1} 加载状态 - readyState: ${video.readyState}, canPlay: ${canPlay}`);
      
      // 一旦可以播放，立即标记为已加载
      if (canPlay) {
        console.log(`[VideoPlayer ${new Date().toISOString()}] ✅ 视频 ${index + 1} 加载完成，可以播放`);
        
        setLoadedVideos(prev => ({
          ...prev,
          [index]: true
        }));
        
        if (index === activeIndexRef.current) {
          if (loadingTimerRef.current) {
            clearTimeout(loadingTimerRef.current);
            loadingTimerRef.current = null;
          }
          
          setIsLoading(false);
          
          // 如果全局播放状态是播放，则自动播放当前视频
          if (isGlobalPlaying && video.paused) {
            // 更快速地尝试播放
            setTimeout(() => {
              const playPromise = video.play().catch(err => {
                console.warn(`[VideoPlayer ${new Date().toISOString()}] ⚠️ 自动播放失败:`, err);
                // 尝试静音播放
                if (err.name === 'AbortError' || err.name === 'NotAllowedError') {
                  video.muted = true;
                  video.play().then(() => {
                    setTimeout(() => { video.muted = false; }, 100);
                  }).catch(() => {});
                }
              });
            }, 10); // 减少延迟
          }
        }
      }
    }, [activeIndex, loadedVideos, isGlobalPlaying]);
    
    // 添加预加载和保活机制
    useEffect(() => {
      const videos = videoRefs.current;
      
      // 预加载视频并保持活跃
      const keepVideosActive = () => {
        videos.forEach((video, index) => {
          if (video) {
            // 设置预加载属性
            video.preload = "auto";
            
            // 如果视频已加载，请求一个视频帧以保持活跃
            if (loadedVideos[index] && index !== activeIndexRef.current) {
              try {
                // 尝试使用现代API请求帧
                if (typeof video.requestVideoFrameCallback === 'function') {
                  video.requestVideoFrameCallback(() => {});
                } else {
                  // 兼容性后备，轻微调整currentTime触发活动
                  const currentTime = video.currentTime;
                  if (currentTime > 0) {
                    video.currentTime = currentTime + 0.001;
                  }
                }
              } catch (e) {
                // 忽略错误
              }
            }
          }
        });
      };
      
      // 每秒执行一次
      const keepAliveInterval = setInterval(keepVideosActive, 1000);
      
      return () => {
        clearInterval(keepAliveInterval);
      };
    }, [activeIndex, loadedVideos]);

    // 添加视频状态监控
    useEffect(() => {
      const activeVideo = videoRefs.current[activeIndexRef.current];
      if (!activeVideo) return;
      
      // 监控缓冲进度
      const handleProgress = () => {
        // 检查已缓冲的范围
        if (activeVideo.buffered.length > 0) {
          const bufferedEnd = activeVideo.buffered.end(activeVideo.buffered.length - 1);
          const duration = activeVideo.duration;
          console.log(`[VideoPlayer ${new Date().toISOString()}] 缓冲进度: ${((bufferedEnd / duration) * 100).toFixed(2)}%`);
        }
      };
      
      // 添加事件监听
      activeVideo.addEventListener('progress', handleProgress);
      
      return () => {
        // 移除事件监听
        activeVideo.removeEventListener('progress', handleProgress);
      };
    }, [activeIndex]);

    useEffect(() => {
      const video = videoRefs.current[activeIndexRef.current];
      if (!video || !videoUrl) return;
    
      // 创建预加载视频元素
      const preloadVideo = document.createElement('video');
      preloadVideo.preload = "auto";
      preloadVideo.style.display = "none";
      document.body.appendChild(preloadVideo);

      const interval = setInterval(() => {
        const vs = useAppStore.getState().videoState;
        const robotId = useAppStore.getState().selection.activeRobotId;
        const timeframe = useAppStore.getState().timeframe;
        const updateVideoState = useAppStore.getState().updateVideoState;
        
        // 获取当前事件对象
        const currentEvent = vs.currentEvent;
        // 使用事件对象的结束时间作为跳转时间
        const resumeTime = currentEvent?.frame_end ?? vs.hlsResumeTime ?? video?.duration ?? video?.currentTime;

        const isActive = activeIndexRef.current === (robotId ? robotId - 1 : 0);
        const isPlayingHls = vs.isHls || videoUrl.endsWith('.m3u8');

        if (!video || video.readyState < 2 || !isPlayingHls || !isActive) return;

        // 当接近结束时，开始预加载原视频
        const isCloseToEnd =
          video.duration > 0 &&
          Math.abs(video.currentTime - video.duration) < 0.5;

        if (isCloseToEnd) {
          const mp4Url = `/${timeframe === "11AM" ? "day" : "night"}/videos/video${activeRobotId}.mp4`;
          
          // 预加载原视频
          if (preloadVideo.src !== mp4Url) {
            preloadVideo.src = mp4Url;
            preloadVideo.currentTime = resumeTime;
            console.log(`[Preload] 🔄 开始预加载原视频 @ ${resumeTime}s`);
          }

          // 当视频真正结束时切换
          if (Math.abs(video.currentTime - video.duration) < 0.2) {
            console.log(`[Fallback] 🎯 自动切换：m3u8 播放完，切回 mp4 @ ${resumeTime}s`);

            // 确保跳转到正确的时间点
            updateVideoState({
              videoUrl: mp4Url,
              time: resumeTime,
              isPlaying: true,
              isHls: false,
              hlsResumeTime: null,
              currentEvent: null
            }, 'fallback_final_hls_end');

            clearInterval(interval);
          }
        }
      }, 300);

      return () => {
        clearInterval(interval);
        // 清理预加载视频
        if (preloadVideo.parentNode) {
          preloadVideo.parentNode.removeChild(preloadVideo);
        }
      };
    }, [videoUrl, activeIndex, activeRobotId, timeframe]);
    
    // 添加视频播放速率优化
    useEffect(() => {
      const activeVideo = videoRefs.current[activeIndexRef.current];
      if (activeVideo) {
        // 设置默认播放速率
        activeVideo.playbackRate = 1.0;
        
        // 监听视频播放速率变化
        const handleRateChange = () => {
          if (activeVideo.playbackRate !== 1.0) {
            activeVideo.playbackRate = 1.0;
          }
        };
        
        activeVideo.addEventListener('ratechange', handleRateChange);
        
        return () => {
          activeVideo.removeEventListener('ratechange', handleRateChange);
        };
      }
    }, [activeIndex]);

    // 处理播放状态变化
    useEffect(() => {
      const video = videoRefs.current[activeIndexRef.current];
      if (!video || isLoading) return;
      
      let playAttemptCount = 0;
      const maxAttempts = 3;
      
      const attemptPlay = async () => {
        if (playAttemptCount >= maxAttempts) {
          console.warn('[VideoPlayer] ⚠️ 达到最大重试次数');
          return;
        }

        try {
          if (videoState.isPlaying && video.paused) {
            playAttemptCount++;
            await video.play();
          } else if (!videoState.isPlaying && !video.paused) {
            video.pause();
          }
        } catch (err) {
          console.warn('[VideoPlayer] ⚠️ 播放失败:', err);
          
          // 特殊处理自动播放限制
          if (err.name === 'NotAllowedError') {
            video.muted = true;
            try {
              await video.play();
              setTimeout(() => {
                video.muted = false;
              }, 100);
            } catch (mutedErr) {
              console.error('[VideoPlayer] ❌ 静音播放也失败:', mutedErr);
            }
            return;
          }

          // 其他错误则延迟重试
          setTimeout(attemptPlay, 100);
        }
      };

      attemptPlay();
    }, [videoState.isPlaying, isLoading]);

    // 暴露方法给父组件
    useImperativeHandle(ref, () => ({
      play: () => {
        const video = videoRefs.current[activeIndexRef.current];
        if (video) {
          video.play().catch(console.warn);
        }
      },
      pause: () => {
        const video = videoRefs.current[activeIndexRef.current];
        if (video) {
          video.pause();
        }
      },
      seekTo: (time) => {
        const video = videoRefs.current[activeIndexRef.current];
        if (video) {
          video.currentTime = time;
        }
      }
    }));
    
    // 重试加载视频
    const handleRetry = useCallback(() => {
      setKey(prev => prev + 1);
      setLoadError(false);
      setIsLoading(true);
      setLoadedVideos({});
    }, []);

    // 获取全局状态
    const { videoState: globalVideoState } = useAppStore();
    const { isPlaying: globalIsPlaying, isHls, hlsBaseTime, hlsResumeTime } = globalVideoState;
    
    // 打印当前视频状态
    // useEffect(() => {
    //   // console.log('[VideoPlayer] 📊 当前视频状态:', {
    //   //   isPlaying: globalIsPlaying,
    //   //   isHls,
    //   //   hlsBaseTime,
    //   //   hlsResumeTime,
    //   //   videoUrl
    //   // });
    // }, [globalVideoState, videoUrl]);
    
    // 优化预加载逻辑
    const startPreload = useCallback((originalVideoUrl, resumeTime) => {
      const preloadState = preloadStateRef.current;
      
      // 防止重复预加载
      if (preloadState.isPreloading && preloadState.startTime === resumeTime) {
        console.log(`[VideoPlayer] ⏭️ 跳过重复预加载 @ ${resumeTime}s`);
        return;
      }
      
      // 清理之前的预加载
      if (preloadState.video) {
        preloadState.video.remove();
        preloadState.video = null;
      }
      
      console.log(`[VideoPlayer] 🚀 开始预加载原视频 @ ${resumeTime}s`);
      preloadState.isPreloading = true;
      preloadState.startTime = resumeTime;
      preloadState.progress = 0;
      
      const preloadVideo = document.createElement('video');
      preloadVideo.preload = "auto";
      preloadVideo.currentTime = resumeTime;
      preloadVideo.src = originalVideoUrl;
      
      // 设置预加载范围
      const preloadRange = 10; // 预加载10秒的数据
      preloadVideo.addEventListener('loadedmetadata', () => {
        const endTime = Math.min(resumeTime + preloadRange, preloadVideo.duration);
        preloadVideo.currentTime = endTime;
      });
      
      preloadVideo.addEventListener('progress', () => {
        const buffered = preloadVideo.buffered;
        if (buffered.length > 0) {
          const progress = (buffered.end(buffered.length - 1) / preloadVideo.duration) * 100;
          preloadState.progress = progress;
          console.log(`[VideoPlayer] 📊 预加载进度: ${progress.toFixed(2)}%`);
          
          // 如果预加载进度足够，可以提前准备切换
          if (progress >= 80 && !preloadVideo.readyState) {
            preloadVideo.load();
          }
        }
      });
      
      preloadVideo.addEventListener('canplay', () => {
        console.log(`[VideoPlayer] ✅ 预加载视频准备就绪 @ ${resumeTime}s`);
        preloadState.video = preloadVideo;
      });
      
      preloadVideo.addEventListener('error', (e) => {
        console.error(`[VideoPlayer] ❌ 预加载视频失败:`, e);
        preloadState.isPreloading = false;
        preloadState.video = null;
      });
      
      preloadState.video = preloadVideo;
    }, []);
    
    // 优化 HLS 结束处理
    useEffect(() => {
      const video = videoRefs.current[activeIndexRef.current];
      if (!video) return;

      const handleHlsEnd = () => {
        if (!videoState.isHls || !videoState.hlsResumeTime) return;

        const preloadVideo = preloadVideoRef.current;
        if (!preloadVideo || preloadVideo.readyState < 3) {
          console.warn('[VideoPlayer] ⚠️ 预加载视频未就绪，等待...');
          return;
        }

        console.log('[VideoPlayer] 🎬 HLS 播放完毕，快速切换到预加载的 MP4');

        // 保存当前状态
        const resumeTime = videoState.hlsResumeTime;
        const wasPlaying = !video.paused;

        // 先暂停当前视频
        video.pause();

        // 设置新的视频源
        video.src = preloadVideo.src;
        
        // 等待视频源加载
        const handleCanPlay = () => {
          // 移除事件监听器
          video.removeEventListener('canplay', handleCanPlay);
          
          // 使用 RAF 确保视觉更新的流畅性
          requestAnimationFrame(() => {
            // 设置正确的时间点
            video.currentTime = resumeTime;

            // 立即更新状态
            updateVideoState({
              videoUrl: preloadVideo.src,
              time: resumeTime,
              isPlaying: wasPlaying,
              isHls: false,
              hlsResumeTime: null,
              hlsBaseTime: 0
            }, 'hls_to_mp4_switch');

            // 如果之前在播放，等待一小段时间后恢复播放
            if (wasPlaying) {
              setTimeout(() => {
                video.play().catch(error => {
                  console.warn('[VideoPlayer] ⚠️ 恢复播放失败:', error);
                });
              }, 100);
            }
          });
        };

        // 添加 canplay 事件监听器
        video.addEventListener('canplay', handleCanPlay);
      };

      video.addEventListener('ended', handleHlsEnd);
      return () => {
        video.removeEventListener('ended', handleHlsEnd);
      };
    }, [videoState.isHls, videoState.hlsResumeTime]);
    
    return (
      <div className="relative w-full h-full flex items-center justify-center bg-black">
        {/* 渲染所有10个视频元素 */}
        {highResVideoFiles.map((url, index) => (
          <video
            key={`${key}-${index}`}
            ref={el => videoRefs.current[index] = el}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "contain",
              background: "black",
              maxHeight: "100vh",
              display: index === activeIndexRef.current ? "block" : "none",
              position: index === activeIndexRef.current ? "relative" : "absolute",
              visibility: index === activeIndexRef.current ? "visible" : "hidden",
            }}
            className={isLoading && index === activeIndexRef.current ? "video-loading" : ""}
            controls={index === activeIndexRef.current}
            muted
            autoPlay
            playsInline
            preload="auto"
            x-webkit-airplay="allow"
            x-webkit-playsinline="true"
            disablePictureInPicture
            controlsList="nodownload nofullscreen noremoteplayback"
            onCanPlay={() => handleVideoLoad(index)}
            onLoadedData={() => handleVideoLoad(index)}
            onEnded={() => {
              if (videoState.isHls && videoState.hlsResumeTime && activeRobotId) {
                const preloadVideo = preloadVideoRef.current;
                if (!preloadVideo) {
                  console.warn(`[VideoPlayer] ⚠️ 预加载视频未就绪，无法切换`);
                  return;
                }
                
                console.log(`[VideoPlayer] 🎬 m3u8 播放完毕，准备切换到预加载的 MP4`);
                
                // 确保预加载视频已经缓冲到足够数据
                if (preloadVideo.readyState >= 2) {
                  const videoElement = videoRefs.current[activeIndexRef.current];
                  if (videoElement) {
                    // 保存当前播放状态
                    const wasPlaying = !videoElement.paused;
                    
                    // 直接使用预加载的视频源
                    videoElement.src = preloadVideo.src;
                    
                    // 等待视频加载完成后再设置时间和播放
                    videoElement.addEventListener('loadedmetadata', () => {
                      videoElement.currentTime = videoState.hlsResumeTime;
                      
                      // 更新状态，但保持 hlsResumeTime
                      updateVideoState({
                        videoUrl: preloadVideo.src,
                        time: videoState.hlsResumeTime,
                        isPlaying: true,
                        isHls: false,
                        hlsResumeTime: videoState.hlsResumeTime, // 保持这个值
                        currentEvent: null
                      }, 'hls_done_resume_mp4');
                      
                      // 恢复播放状态
                      if (wasPlaying) {
                        videoElement.play().catch(err => {
                          console.warn(`[VideoPlayer] ⚠️ 切换后播放失败:`, err);
                        });
                      }
                    }, { once: true });
                  }
                }
              }
            }}
            

            
            onError={(e) => {
              console.error(`视频 ${index + 1} 加载错误: ${url}`, e.target.error);
              
              if (index === activeIndexRef.current) {
                setLoadError(true);
                setIsLoading(false);
              }
            }}
          >
            {url && <source src={url} type="video/mp4" />}
            您的浏览器不支持视频标签。
          </video>
        ))}

        {/* 加载指示器 */}
        {isLoading && (
          <div className="loading-indicator absolute inset-0 flex flex-col items-center justify-center bg-black bg-opacity-50 pointer-events-none">
            <div className="spinner w-10 h-10 border-4 border-t-blue-500 border-gray-300 rounded-full animate-spin"></div>
            <p className="text-white mt-2">加载中...</p>
          </div>
        )}

        {/* 错误提示 */}
        {loadError && (
          <div className="error-message absolute inset-0 flex flex-col items-center justify-center bg-black bg-opacity-50">
            <p className="text-white mb-2">视频加载失败，请重试。</p>
            <button 
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
              onClick={handleRetry}
            >
              重试
            </button>
          </div>
        )}

        {/* 展开/收起按钮 */}
        {!hideExpandButton && onToggleExpand && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onToggleExpand();
            }}
            style={{
              position: "absolute",
              top: "8px",
              right: "8px",
              width: "32px",
              height: "32px",
              backgroundColor: "rgba(0, 0, 0, 0.7)",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              border: "1px solid rgba(255, 255, 255, 0.2)",
              zIndex: 1000,
            }}
          >
            {activeIndexRef.current === 0 ? (
              <Minimize2
                style={{ color: "white", width: "20px", height: "20px" }}
              />
            ) : (
              <Maximize2
                style={{ color: "white", width: "20px", height: "20px" }}
              />
            )}
          </button>
        )}
      </div>
    );
  }
);

export default VideoPlayer;